import { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../../../components/form/Search";
import Button from "../../../../components/form/Button";
import Table from "../../../../components/elements/table/Table";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { userDelete, userList, userStatusChange } from "../../../../services/userService";
import Switch from "../../../../components/form/Switch";
import Badge from "../../../../components/elements/Badge";
import { classNames } from "../../../../helpers/classNames";
import dayjs from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ManageUserDrawer from "../../../../components/sections/admin/user/drawer/ManageUserDrawer";
import { activityList } from "../../../../services/activityService";

const ActivityList = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ["activity", filters, search],
    queryFn: async () => {
      const { role = [], statusCode = [], ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),
        ...Object.fromEntries([...role].map((val, i) => [`role[${i}]`, val])),
        // ...Object.fromEntries(
        //   [...statusCode].map((val, i) => [`statusCode[${i}]`, val])
        // ),
      };

      const res = await activityList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || {};
    },
  });

  const options = [
    { id: "email", icon: "fa-regular fa-envelope", tooltip: "Resend Email" },
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  // add or edit user =================>
  function userManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["activity", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [res?.data, ...stateData];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["activity", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["activity", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["activity", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   testimonials status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["activity", filters, search], (state) => {
        if (state) {
          let stateData = JSON.parse(JSON.stringify(state?.data));
          stateData = (stateData || []).map((elm) =>
            elm?._id === id ? { ...elm, status: elm?.status === 1 ? 2 : 1 } : elm
          );
          return { ...state, data: stateData };
        }
      });
      const res = await userStatusChange({
        ids: id,
        status,
      });
      return { res, payload: { id, status } };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["activity", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || []).map((elm) =>
              elm?._id === payload?.id ? { ...elm, status: elm?.status === 1 ? 2 : 1 } : elm
            );
            return { ...state, data: stateData };
          }
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const tableHeadData = [
    {
      _id: "name",
      name: "Name",
      align: "left",
    },
    {
      _id: "email",
      name: "Email",
      align: "left",
    },
    {
      _id: "activityType",
      name: "Activity Type",
      align: "left",
      isFilter: true,
      isFilterSearch: true, // You can enable/disable search in filter
      // onFilter: (val) => {
      //   if (val?.length > 0) {
      //     setFilters((pre) => ({
      //       ...pre,
      //       activityType: val.map((item) => item.value),
      //       page: 1,
      //     }));
      //   } else {
      //     setFilters((pre) => {
      //       delete pre.activityType;
      //       return { ...pre };
      //     });
      //   }
      // },
      filterData: [
        { name: "USER_UPDATE", value: "USER_UPDATE", type: "checkbox" },
        { name: "PAGE_VIEW", value: "PAGE_VIEW", type: "checkbox" },
        { name: "LOGIN", value: "LOGIN", type: "checkbox" },
        { name: "SETTINGS_UPDATE", value: "SETTINGS_UPDATE", type: "checkbox" },
        { name: "USER_CREATE", value: "USER_CREATE", type: "checkbox" },
        {
          name: "USER_ASSIGN_TO_GROUP",
          value: "USER_ASSIGN_TO_GROUP",
          type: "checkbox",
        },
        {
          name: "USER_UNASSIGN_TO_GROUP",
          value: "USER_UNASSIGN_TO_GROUP",
          type: "checkbox",
        },
      ],
    },
    {
      _id: "description",
      name: "Description",
      align: "left",
    },
    {
      _id: "date",
      name: "Date",
      align: "left",
    },
    {
      _id: "location",
      name: "Location/IP Address",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => ({
    _id: item._id,
    rowData: [
      {
        _id: "name",
        type: "user",
        align: "left",
        data: item?.actorDetails?.firstName + " " + item?.actorDetails?.lastName,
      },
      {
        _id: "emai;",
        type: "text",
        align: "left",
        data: item?.actorDetails?.email,
      },
      {
        _id: "activityType",
        type: "text",
        align: "left",
        data: item.action,
      },
      {
        _id: "description",
        type: "text",
        align: "left",
        data: item.description || "-",
      },
      {
        _id: "date",
        type: "text",
        align: "left",
        data: dayjs(item.date).format("DD MMM, YYYY"),
      },
      {
        _id: "location",
        type: "text",
        align: "left",
        data: (
          <div>
            <p>{item.visibility || "-"}</p>
            <p>{item.ip || "-"}</p>
          </div>
        ),
      },
    ],
  }));
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await userDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["user", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["user", filters, search]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="flex items-center gap-3">
        <div className="w-36">
          <LimitSelect
            value={filters?.limit}
            allValue={data?.metadata?.totalDocs}
            onChange={(val) => handleFilterChange("limit", val, setFilters)}
          />
        </div>
        <div className="w-72">
          <Search
            placeholder={"Search here"}
            search={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
        </div>
        {/* <div className="relative ml-auto">
          <Button
            buttonIcon={"fa-regular fa-plus"}
            buttonLabel={"Create New"}
            buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
          />
        </div> */}
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        tableActionDropdownContainer={"!w-48"}
      />
      <ManageUserDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={userManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default ActivityList;
